import { Preferences } from '@capacitor/preferences';
import { isNative } from './platform.js';
import { setAuthToken } from './apiClient.js';

/**
 * Persistencia durable del Bearer token de la sesión nativa (Capacitor) — Fase 23,
 * Bloque B. Cierra el lazo que A.3 dejó abierto: `apiClient` guarda el token
 * **en memoria** (`setAuthToken`), pero sin esto la sesión nativa no sobrevive al
 * reinicio de la app. Acá lo respaldamos en `@capacitor/preferences` (storage
 * durable del SO) y sincronizamos las dos copias.
 *
 * **Web**: todas las funciones son **no-op** (`isNative === false`). La web no
 * usa Bearer ni storage — la sesión viaja en la cookie httpOnly same-origin. Así
 * nunca tocamos Preferences (que en web caería a localStorage) y el comportamiento
 * web queda idéntico. Ver `docs/speak-plan/23-mobile-capacitor.md` §3.2.
 *
 * v1 usa `@capacitor/preferences` (no cifrado). Endurecer a Keychain /
 * EncryptedSharedPreferences con un plugin de secure storage es trabajo futuro
 * (anotar en IMPROVEMENTS si se posterga).
 */

/**
 * Clave del token en Preferences. No comparte namespace con la cookie web
 * (`orborix.session-token`) — son mecanismos distintos en plataformas distintas —
 * pero usamos el mismo nombre por trazabilidad.
 */
const TOKEN_KEY = 'orborix.session-token';

/**
 * Las ops de `@capacitor/preferences` son **fail-soft**: un fallo del storage
 * nativo (bridge, plugin, disco) NO debe colgar el boot, abortar un login que el
 * backend ya validó, ni dejar el logout a medias. Por eso la copia **in-memory**
 * se actualiza SIEMPRE primero (refleja la sesión de esta corrida) y la
 * persistencia durable se intenta después, capturando el reject (log + seguir).
 *
 * Esto **no** viola la regla fail-fast del repo: no enmascara una mala config ni
 * cambia qué se cobra / qué corre — es recuperación de IO sobre una durabilidad
 * best-effort (la sesión se re-persiste en el próximo login). La regla fail-fast
 * aplica a la **resolución de valores** (url/modelo/key/env), no a un write de
 * storage que falla.
 */

/**
 * Boot nativo: hidrata el token desde el storage durable hacia la copia in-memory
 * de `apiClient`, **antes** de la primera llamada autenticada (`me()`). No-op en web.
 * Si la lectura falla, degrada a "sin sesión" → `me()` cae a 401 → `/login`
 * (recuperable), en vez de colgar la app en el splash.
 */
export async function loadNativeAuthToken(): Promise<void> {
  if (!isNative) return;
  try {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    setAuthToken(value ?? null);
  } catch (err) {
    console.warn('[nativeAuth] no se pudo leer el token de Preferences', err);
    setAuthToken(null);
  }
}

/**
 * Persiste el token tras un login nativo (OTP verify): in-memory primero, luego
 * Preferences (fail-soft). No-op en web. Llamado con el `token` que el backend
 * devuelve en el body cuando llega `X-Orborix-Native: 1`.
 */
export async function persistNativeAuthToken(token: string): Promise<void> {
  if (!isNative) return;
  setAuthToken(token);
  try {
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } catch (err) {
    // Login YA exitoso server-side: un fallo de persistencia degrada a "sesión
    // válida esta corrida pero no sobrevive al reinicio", no tira el login.
    console.warn('[nativeAuth] no se pudo persistir el token en Preferences', err);
  }
}

/**
 * Signout nativo: borra el token de la copia in-memory primero (el logout local
 * nunca queda a medias) y del storage durable después (fail-soft). No-op en web
 * (la cookie la revoca el backend en `/api/auth/signout`).
 */
export async function clearNativeAuthToken(): Promise<void> {
  if (!isNative) return;
  setAuthToken(null);
  try {
    await Preferences.remove({ key: TOKEN_KEY });
  } catch (err) {
    console.warn('[nativeAuth] no se pudo borrar el token de Preferences', err);
  }
}
